import React, { Suspense, useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  BrowserRouter,
  Route,
  Router,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import PrivateOutlet from './layout/PrivateOutlet';
import PublicOutlet from './layout/PublicOutlet';
import Loading from './components/Loading';
import useInplaySocket from './hooks/useInplaySocket';
import store, { saveState } from './redux/store';
import {
  CASINO_SERVICE_URL,
  createSocket,
  CRICKET_SERVICE_URL,
  IS_PROD,
  SOCCER_AND_TENNIS_SERVICE_URL,
} from './utils/constants';
import { usePrefersReducedMotion } from '@chakra-ui/react';

import { getService } from './utils/commonFunctions';
import { setUserData, setAnnouncement } from './redux/features/accountSlice';
import { getAnnouncementAPI } from './service/AuthService';
import { PARENT_SOURCE_TAG } from './utils/webviewBridge';
import { getUserDataAPI } from './service/UserService';

// Lazy-loaded page/component imports
const ActivityLog = React.lazy(() => import('./pages/activityLog/ActivityLog'));
const BetHistory = React.lazy(() => import('./pages/betHistory/BetHistory'));
const CasinoList = React.lazy(() => import('./pages/casino/CasinoList'));
const DiamondCasinoGame = React.lazy(() => import('./pages/casino/DiamondCasinoGame'));
const Cards32A = React.lazy(() => import('./pages/casino/games/32Cards-A'));
const Cards32B = React.lazy(() => import('./pages/casino/games/32Cards-B'));
const JudgementCards3 = React.lazy(() => import('./pages/casino/games/3CardsJudgement'));
const AmarAkbarAnthony = React.lazy(() => import('./pages/casino/games/AmabAkbarAnthony'));
const AndarBahar = React.lazy(() => import('./pages/casino/games/AndarBahar'));
const AndarBahar2 = React.lazy(() => import('./pages/casino/games/AndarBahar-2'));
const BollywoodCasino = React.lazy(() => import('./pages/casino/games/BollywoodCasino'));
const CasinoMeter = React.lazy(() => import('./pages/casino/games/CasinoMeter'));
const CasinoQueen = React.lazy(() => import('./pages/casino/games/CasinoQueen'));
const DragonTiger1Day = React.lazy(() => import('./pages/casino/games/DragonTiger1Day'));
const DragonTiger202 = React.lazy(() => import('./pages/casino/games/DragonTiger202'));
const DragonTiger201 = React.lazy(() => import('./pages/casino/games/DragonTiger201'));
const InstantWorli = React.lazy(() => import('./pages/casino/games/InstantWorli'));
const Lucky7A = React.lazy(() => import('./pages/casino/games/Lucky7A'));
const Lucky7B = React.lazy(() => import('./pages/casino/games/Lucky7B'));
const Poker20 = React.lazy(() => import('./pages/casino/games/Poker20-20'));
const Race2020 = React.lazy(() => import('./pages/casino/games/Race2020'));
const TeenPatti = React.lazy(() => import('./pages/casino/games/TeenPatti'));
const TeenPatti1Day = React.lazy(() => import('./pages/casino/games/Teenpatti1Day'));
const CasinoResult = React.lazy(() => import('./pages/casinoResults/CasinoResults'));
const ChangePassword = React.lazy(() => import('./pages/ChangePassword'));
const ComingSoon = React.lazy(() => import('./pages/ComingSoon'));
const Dashboard = React.lazy(() => import('./pages/dashboard'));
const GamePage = React.lazy(() => import('./pages/game/GamePage'));
const ProfitLossReport = React.lazy(() => import('./pages/profitLossReport/ProfitLossReport'));
const UnSetteledBet = React.lazy(() => import('./pages/unSetteledBet/UnSetteledBet'));
const Video = React.lazy(() => import('./pages/Video'));
const DragonTigerLion = React.lazy(() => import('./pages/casino/games/DragonTigerLion'));
const TeenPattiTest = React.lazy(() => import('./pages/casino/games/TeenPattiTest'));
const TeenPattiOpen = React.lazy(() => import('./pages/casino/games/TeenPattiOpen'));
const CasinoWar = React.lazy(() => import('./pages/casino/games/CasinoWar'));
const Five5Cricket = React.lazy(() => import('./pages/casino/games/Five-5Cricket'));
const Cricket2020 = React.lazy(() => import('./pages/casino/games/Cricket20-20'));
const Poker1Day = React.lazy(() => import('./pages/casino/games/Poker1Day'));
const Pokerr6Players = React.lazy(() => import('./pages/casino/games/Poker6Players'));
const Baccart = React.lazy(() => import('./pages/casino/games/Baccart'));
const Baccart2 = React.lazy(() => import('./pages/casino/games/Baccarat2'));
const Worli = React.lazy(() => import('./pages/casino/games/WorliMatka'));
const SuperOver = React.lazy(() => import('./pages/casino/games/SuperOver'));
const Login = React.lazy(() => import('./pages/login'));
const SecurityAuth = React.lazy(() => import('./pages/settings/SecurityAuth'));
const LoginAuth = React.lazy(() => import('./pages/login/loginAuth'));
const ChangeButtonValue = React.lazy(() => import('./pages/ChangeButtonValue'));
const ChangePasswordPage = React.lazy(() => import('./pages/ChangePasswordPage'));
const LastResults = React.lazy(() => import('./components/LastResults'));
const AccountStatement = React.lazy(() => import('./components/AccountStatement').then(m => ({ default: m.AccountStatement })));
const CurrentBets = React.lazy(() => import('./components/Currentbets').then(m => ({ default: m.CurrentBets })));
const LiveCasinoBets = React.lazy(() => import('./components/LiveCasinoBet').then(m => ({ default: m.LiveCasinoBets })));
const FAQ = React.lazy(() => import('./components/FAQ').then(m => ({ default: m.FAQ })));
const FinalCasinoResults = React.lazy(() => import('./components/CasinoResults').then(m => ({ default: m.FinalCasinoResults })));
const FinancialCasinoResult = React.lazy(() => import('./components/CasinoResults').then(m => ({ default: m.FinancialCasinoResult })));
const UserDashBoardPage = React.lazy(() => import('./pages/UserDashBoardPage').then(m => ({ default: m.UserDashBoardPage })));
const UserDetails = React.lazy(() => import('./pages/UserDetalisPage').then(m => ({ default: m.UserDetails })));
const SendMoney = React.lazy(() => import('./components/SendMoney').then(m => ({ default: m.SendMoney })));
const LandingPage = React.lazy(() => import('./pages/LandingPage').then(m => ({ default: m.LandingPage })));
const ProfilePage = React.lazy(() => import('./pages/ProfilePage'));
const BetsHistory = React.lazy(() => import('./components/BetHistory').then(m => ({ default: m.BetsHistory })));
const PriceLabelForm = React.lazy(() => import('./components/PriceLabelForm'));
const SignUpCard = React.lazy(() => import('./components/login/signUp'));
const SignUp = React.lazy(() => import('./pages/login/signUp'));
const ForgotPassword = React.lazy(() => import('./pages/forgotPassword'));
const CasinotBets = React.lazy(() => import('./components/CasinoBets').then(m => ({ default: m.CasinotBets })));
const CardGamesList = React.lazy(() => import('./pages/casino/CardGamesList'));
const MyDepositSection = React.lazy(() => import('./pages/wallet/MyDeposits/MyDepositSection'));
const MywithdrawlSec = React.lazy(() => import('./pages/wallet/MyWithdrawls/MywithdrawlSec'));
const DepositSection = React.lazy(() => import('./pages/wallet/DepositCredit/DepositSection'));
const DepositMain = React.lazy(() => import('./pages/wallet/DepositCredit/DepositMain'));
const Deposite = React.lazy(() => import('./pages/deposite'));
const Withdrawal = React.lazy(() => import('./pages/withdrawal'));
const DepositHistory = React.lazy(() => import('./pages/depositHistory').then(m => ({ default: m.DepositHistory })));
const WithdrawalHistory = React.lazy(() => import('./pages/withdrawalHistory').then(m => ({ default: m.WithdrawalHistory })));
const DirectUsers = React.lazy(() => import('./pages/directUsers').then(m => ({ default: m.DirectUsers })));
const AddNewUser = React.lazy(() => import('./pages/addNewMember').then(m => ({ default: m.AddNewUser })));
const BankAccount = React.lazy(() => import('./pages/bankAccount'));
const SSOLogin = React.lazy(() => import('./pages/sso'));

export default function App() {
  const dispatch = useDispatch();
  const userData = useSelector((state) => state.account.userData);
  const isLoading = useSelector((state) => state.account.loading);
  const prefersMotion = usePrefersReducedMotion();
  const location = useLocation();
  const navigate = useNavigate();
  const { sportsName } = useParams();
  const gameKey = useSelector((state) => state.gameData.gameKey);
  const permissions = JSON.parse(localStorage.getItem('state'));
  const canAddNewUser = permissions?.account?.userData?.canCreateUser;
  const canUseBanking = permissions?.account?.userData?.canUseBanking;

  useInplaySocket(true);

  useEffect(() => {
    if (location.pathname.includes('live-casino')) {
      if (gameKey) {
        createSocket(
          'casino',
          dispatch,
          gameKey,
          getService(location.pathname).port,
          getService(location.pathname).service
        );
      }
    }
  }, [gameKey, dispatch]);

  useEffect(() => {
    if (userData?.token && userData?.resetRequired) {
      dispatch({
        type: 'accountData/setChangePopSate',
        payload: true,
      });
    }
  }, [userData]);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user'));
    if (user) {
      dispatch({
        type: 'accountData/setUserData',
        payload: user,
      });
    }
  }, []);

  useEffect(() => {
    let saveTimer = null;
    const unsubscribe = store.subscribe(() => {
      if (saveTimer) return;
      saveTimer = setTimeout(() => {
        saveState(store.getState());
        saveTimer = null;
      }, 2000);
    });
    return () => {
      unsubscribe();
      if (saveTimer) {
        clearTimeout(saveTimer);
        saveState(store.getState());
      }
    };
  }, []);

  useEffect(() => {
    const fetchAnnouncement = async () => {
      try {
        const res = await getAnnouncementAPI();
        if (res?.status === 200) {
          dispatch(setAnnouncement(res.data?.response?.announcement || ''));
        }
      } catch {
        // Announcement fetch failed — non-critical
      }
    };
    fetchAnnouncement();
  }, [dispatch]);

  useEffect(() => {
    const handleMessage = async (event) => {
      const data = event.data;
      if (!data || data.source !== PARENT_SOURCE_TAG) return;

      switch (data.type) {
        case 'LOGOUT': {
          dispatch({ type: 'gameData/logOut' });
          dispatch({ type: 'accountData/logOut' });
          localStorage.clear();
          break;
        }
        case 'REFRESH_BALANCE': {
          const user = JSON.parse(localStorage.getItem('user'));
          if (user?.id) {
            try {
              const res = await getUserDataAPI(user.id);
              if (res && res?.code === 200) {
                dispatch({ type: 'accountData/setUserProfile', payload: res.response });
              }
            } catch {
              // Balance refresh failed — non-critical
            }
          }
          break;
        }
        case 'NAVIGATE': {
          if (data.payload?.path) {
            navigate(data.payload.path);
          }
          break;
        }
        default:
          break;
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [dispatch]);


  // Shared casino routes used by both authenticated and public views
  const casinoRoutes = (
    <>
      <Route path='/live-casino' element={<CasinoList />} />
      <Route path='/card-games' element={<CardGamesList />} />
      <Route path='/live-casino/teenpatti/20-20' element={<TeenPatti />} />
      <Route path='/live-casino/teenpatti/1-day' element={<TeenPatti1Day />} />
      <Route path='/live-casino/teenpatti/test' element={<TeenPattiTest />} />
      <Route path='/live-casino/teenpatti/open' element={<TeenPattiOpen />} />
      <Route path='/live-casino/dragon-tiger/20-20' element={<DragonTiger201 />} />
      <Route path='/live-casino/dragon-tiger/20-20/2' element={<DragonTiger202 />} />
      <Route path='/live-casino/card-32/a' element={<Cards32A />} />
      <Route path='/live-casino/card-32/b' element={<Cards32B />} />
      <Route path='/live-casino/race/20-20' element={<Race2020 />} />
      <Route path='/live-casino/cn/queen' element={<CasinoQueen />} />
      <Route path='/live-casino/dragon-tiger/1-day' element={<DragonTiger1Day />} />
      <Route path='/live-casino/lucky7/a' element={<Lucky7A />} />
      <Route path='/live-casino/lucky7/b' element={<Lucky7B />} />
      <Route path='/live-casino/andar-bahar' element={<AndarBahar />} />
      <Route path='/live-casino/andar-bahar/2' element={<AndarBahar2 />} />
      <Route path='/live-casino/3cardsjudgement' element={<JudgementCards3 />} />
      <Route path='/live-casino/cn/bollywood-table' element={<BollywoodCasino />} />
      <Route path='/live-casino/andar-bahar/aaa' element={<AmarAkbarAnthony />} />
      <Route path='/live-casino/poker/20-20' element={<Poker20 />} />
      <Route path='/live-casino/worli/2' element={<InstantWorli />} />
      <Route path='/live-casino/cn/meter' element={<CasinoMeter />} />
      <Route path='/live-casino/dragon-tiger/dtl' element={<DragonTigerLion />} />
      <Route path='/live-casino/cn/war' element={<CasinoWar />} />
      <Route path='/live-casino/cricket/five' element={<Five5Cricket />} />
      <Route path='/live-casino/cricket/20-20' element={<Cricket2020 />} />
      <Route path='/live-casino/poker' element={<Poker1Day />} />
      <Route path='/live-casino/poker/poker-6-player' element={<Pokerr6Players />} />
      <Route path='/live-casino/baccarat' element={<Baccart />} />
      <Route path='/live-casino/baccarat/2' element={<Baccart2 />} />
      <Route path='/live-casino/worli' element={<Worli />} />
      <Route path='/live-casino/cricket/super-over' element={<SuperOver />} />
      {/* Legacy public casino paths */}
      <Route path='/live-casino/race20' element={<Race2020 />} />
      <Route path='/live-casino/lucky7a' element={<Lucky7A />} />
      <Route path='/live-casino/lucky7b' element={<Lucky7B />} />
      <Route path='/live-casino/btable' element={<BollywoodCasino />} />
      <Route path='/live-casino/aaa' element={<AmarAkbarAnthony />} />
      <Route path='/live-casino/worli2' element={<InstantWorli />} />
      <Route path='/live-casino/cmeter' element={<CasinoMeter />} />
      <Route path='/live-casino/war' element={<CasinoWar />} />
      <Route path='/live-casino/cricketv3' element={<Five5Cricket />} />
      <Route path='/live-casino/cmatch20' element={<Cricket2020 />} />
    </>
  );

  if (location.pathname === '/sso') {
    return (
      // <Suspense fallback={<Loading />}>
      // </Suspense>
        <SSOLogin />
    );
  }

  return userData?.token ? (
    <PrivateOutlet>
      <Suspense fallback={<Loading />}>
        <Routes>
          <Route exact path='/' element={<LandingPage />} />
          <Route exact path='/live-games' element={<LandingPage defaultFilter='live' />} />
          <Route exact path='/games/:sportName' element={<Dashboard />} />
          <Route exact path='/profile' element={<ProfilePage />} />
          <Route exact path='/game/:game/:id' element={<GamePage />} />
          {casinoRoutes}
          <Route path='/diamond-casino/play' element={<DiamondCasinoGame />} />
          <Route path='/report/account-statement' element={<AccountStatement />} />
          <Route path='/report/bet-history' element={<BetHistory />} />
          <Route path='/report/profit-loss-report' element={<ProfitLossReport />} />
          <Route path='/report/unsetteled-bet' element={<UnSetteledBet />} />
          <Route path='/report/activity-log' element={<ActivityLog />} />
          <Route path='/report/casino-result' element={<CasinoResult />} />
          <Route path='/report/changebtnvalue' element={<ProfilePage />} />
          <Route path='/report/changepassword' element={<ProfilePage />} />
          <Route path='/settings/secureauth' element={<SecurityAuth />} />
          <Route path='/account' element={<AccountStatement />} />
          <Route path='/current' element={<CurrentBets />} />
          <Route path='/casino-bets' element={<CasinotBets />} />
          <Route path='/FAQ' element={<FAQ />} />
          <Route path='/casino-result' element={<FinancialCasinoResult />} />
          <Route path='/dashboard' element={<UserDashBoardPage />} />
          <Route path='/user-details' element={<UserDetails />} />
          <Route path='/bet_history' element={<BetsHistory />} />
          <Route path='/change-password' element={<ProfilePage />} />
          {canUseBanking && (
            <>
              <Route path='/sendMoney' element={<SendMoney />} />
              <Route path='/deposit' element={<Deposite />} />
              <Route path='/withdrawal' element={<Withdrawal />} />
              <Route path='/bank-account' element={<BankAccount />} />
              <Route path='/deposit-history' element={<DepositHistory />} />
              <Route path='/withdrawal-history' element={<WithdrawalHistory />} />
            </>
          )}
          {canAddNewUser && (
            <>
              <Route path='/add-newUser' element={<AddNewUser />} />
              <Route path='/direct-users' element={<DirectUsers />} />
            </>
          )}
          <Route exact path='/my-deposits' element={<MyDepositSection />} />
          <Route exact path='/my-withdrawals' element={<MywithdrawlSec />} />
          <Route exact path='/deposit-credit' element={<DepositSection />} />
          <Route exact path='/deposit-modal' element={<DepositMain />} />
        </Routes>
      </Suspense>
    </PrivateOutlet>
  ) : location.pathname === '/sign-up' ? (
    <Suspense fallback={<Loading />}>
      <SignUpCard />
    </Suspense>
  ) : (
    <PublicOutlet>
      <Suspense fallback={<Loading />}>
        <Routes>
          {/* Home page - visible without login */}
          <Route exact path='/' element={<LandingPage />} />
          <Route exact path='/home' element={<LandingPage />} />
          <Route exact path='/live-games' element={<LandingPage defaultFilter='live' />} />
          <Route exact path='/games/:sportName' element={<Dashboard />} />
          <Route path='/game/:game/:id' element={<GamePage />} />
          <Route exact path='video' element={<Video />} />
          {casinoRoutes}

          {/* Auth routes: render LandingPage behind + modal overlay on top */}
          <Route path='/login' element={<><LandingPage /><Login /></>} />
          <Route path='/forgot-password' element={<><LandingPage /><ForgotPassword /></>} />
          <Route path='/loginauth/2' element={<><LandingPage /><LoginAuth /></>} />
        </Routes>
      </Suspense>
    </PublicOutlet>
  );
}
